import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Briefcase, MapPin, Calendar, ExternalLink } from 'lucide-react';
import { workExperience } from '../../mock';

const BoldExperienceSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="experience" className="py-32 px-6 relative bg-[#1A1430]/30">
      {/* Section Watermark */}
      <div className="section-watermark" style={{ top: '5%', right: '-5%' }}>02</div>
      
      <div className="max-w-6xl mx-auto" ref={ref}>
        {/* Heading */}
        <motion.h2
          className="text-center mb-24 gradient-text"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          Where I've made<br />an impact
        </motion.h2>
        
        {/* Animated Timeline */}
        <div className="relative">
          {/* Glowing Timeline Line */}
          <div className="absolute left-0 md:left-12 top-0 bottom-0 w-1 bg-gradient-to-b from-[#7C3AED] via-[#C026D3] to-transparent rounded-full" />
          
          {workExperience.map((exp, index) => (
            <motion.div
              key={exp.id}
              className={`relative pl-10 md:pl-28 pb-20 last:pb-0 ${
                index % 2 === 0 ? 'md:pr-0' : 'md:pr-0'
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              {/* Timeline Dot */}
              <div className="absolute left-0 md:left-[45px] top-3 w-6 h-6 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#C026D3] border-4 border-[#0E0B1F] shadow-lg shadow-[#7C3AED]/50" />
              
              {/* Experience Card */}
              <motion.div
                className={`p-8 rounded-3xl border-2 transition-all duration-300 ${
                  exp.current
                    ? 'bg-gradient-to-br from-[#7C3AED]/10 to-[#C026D3]/10 border-[#7C3AED]'
                    : 'bg-[#1A1430] border-[#7C3AED]/20 hover:border-[#7C3AED]/50'
                }`}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-3xl font-bold text-[#EDE9FE] mb-3" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
                      {exp.company}
                    </h3>
                    <p className="text-xl text-[#FF5C39] font-semibold mb-3">
                      {exp.position}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-[#9D97B5]">
                      <span className="flex items-center gap-2">
                        <MapPin size={16} className="text-[#7C3AED]" />
                        {exp.location}
                      </span>
                      <span className="flex items-center gap-2">
                        <Calendar size={16} className="text-[#7C3AED]" />
                        {exp.duration}
                      </span>
                    </div>
                  </div>
                  
                  {/* Current Badge with Gradient */}
                  {exp.current && (
                    <motion.span
                      className="px-5 py-2 bg-gradient-to-r from-[#7C3AED] to-[#C026D3] text-white rounded-full text-sm font-bold shadow-lg"
                      animate={{
                        boxShadow: [
                          '0 0 20px rgba(124, 58, 237, 0.5)',
                          '0 0 40px rgba(192, 38, 211, 0.8)',
                          '0 0 20px rgba(124, 58, 237, 0.5)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      Current
                    </motion.span>
                  )}
                </div>
                
                {/* Website Link */}
                {exp.website && (
                  <a
                    href={`https://${exp.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#7C3AED] hover:text-[#FF5C39] mb-6 transition-colors font-medium"
                  >
                    <ExternalLink size={18} />
                    {exp.website}
                  </a>
                )}
                
                {/* Responsibilities */}
                <ul className="space-y-4 mt-6">
                  {exp.responsibilities.map((responsibility, idx) => (
                    <motion.li
                      key={idx}
                      className="flex gap-4 text-[#9D97B5] leading-relaxed"
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.5, delay: index * 0.2 + idx * 0.1 }}
                    >
                      <span className="text-[#FF5C39] mt-1.5 flex-shrink-0 font-bold">→</span>
                      <span className="text-[#EDE9FE]">{responsibility}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BoldExperienceSection;